import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import {
  Label,
  Input,
  FormMessage,
  Box,
  Alert,
  Flex,
  Button,
  Panel,
  theme,
  Text,
  Link,
  Select,
} from '@xometry/xometry_loft';
import { css, SerializedStyles } from '@emotion/react';
import ReactSelect from 'react-select';
import countryListUtil from '@xometry/npm_package_country_list';
import states from '../../../utils/states-list';
import {
  billingProfileInitialValues,
  billingProfileSchema,
} from '../formikSetup';
import { s, reactSelectStyle, selectTheme } from './Form.style';
import TooltipBlueIcon from '../../../components/TooltipBlueIcon';

const billingProfile__panel = css`
  max-width: 1200px;
  margin-top: 20px;
  overflow-x: auto;
`;

const stateOptions: any[] = [];
for (const stateObj of states) {
  stateOptions.push(
    <option
      label={stateObj.label}
      value={stateObj.value}
      key={`${stateObj.label}-${stateObj.value}`}
    />
  );
}

type FormGroupType = {
  name?: string;
  displayName?: string;
  value?: string;
  error?: any;
  touched?: boolean | any | undefined;
  handleChange?: any;
  inputWidth?: any;
  boxStyle?: SerializedStyles[];
  placeholder?: string;
  tooltipInfo?: string;
  className?: string;
};

const FormGroup = ({
  name,
  displayName,
  value = '',
  error,
  touched,
  handleChange,
  inputWidth,
  boxStyle,
  placeholder,
  tooltipInfo,
  className,
}: FormGroupType) => (
  <Box css={boxStyle} mb={2}>
    <Flex>
      <Label htmlFor={name}>{displayName}</Label>
      {tooltipInfo && <TooltipBlueIcon>{tooltipInfo}</TooltipBlueIcon>}
    </Flex>
    <Input
      type="text"
      id={name}
      className={className}
      value={value}
      placeholder={placeholder}
      onChange={handleChange}
      css={inputWidth}
      invalid={Boolean(error && touched)}
    />
    {error && touched && (
      <FormMessage invalid mt={1}>
        {error}
      </FormMessage>
    )}
  </Box>
);

const BillingProfileForm = (props: any) => {
  const {
    onSave,
    onCancelClick,
    submitErrors,
    billingProfile,
    showMode,
    loading,
  } = props;
  const [showStreet2, setShowStreet2] = useState(false);

  const isEdit = showMode === 'Edit';

  const billingProfileValues = {
    name: billingProfile ? billingProfile.name : '',
    apEmailAddress: billingProfile ? billingProfile.apEmailAddress : '',
    contact: {
      name: billingProfile ? billingProfile.contact?.name : '',
      emailAddress: billingProfile ? billingProfile.contact?.emailAddress : '',
      phoneNumber: billingProfile ? billingProfile.contact?.phoneNumber : '',
      address: {
        street1: billingProfile ? billingProfile.contact?.address?.street1 : '',
        street2: billingProfile ? billingProfile.contact?.address?.street2 : '',
        city: billingProfile ? billingProfile.contact?.address?.city : '',
        state: billingProfile ? billingProfile.contact?.address?.state : '',
        province: billingProfile
          ? billingProfile.contact?.address?.province
          : '',
        zipcode: billingProfile ? billingProfile.contact?.address?.zipcode : '',
        country: billingProfile
          ? billingProfile.contact?.address?.country
          : 'United States',
      },
    },
  };

  return (
    <Panel
      kind="primary"
      css={[
        billingProfile__panel,
        css`
          display: inline-block;
          overflow-x: visible;
        `,
      ]}
    >
      <Formik
        initialValues={
          isEdit ? billingProfileValues : billingProfileInitialValues
        }
        onSubmit={onSave}
        validationSchema={billingProfileSchema}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form css={s.form}>
            <Text
              kind="panel-title"
              display="block"
              fontWeight={theme.fontWeights.semibold}
              marginBottom={theme.space[1]}
            >
              Billing Profile
            </Text>
            <Text kind="body-sm" marginBottom={theme.space[4]} display="block">
              Fill out the information below for your Accounts Payable Team to
              access the credit application, pay by purchase order, or verify
              your tax-exempt status.
            </Text>
            {/* Don't allow user to edit these fields */}
            {!isEdit && (
              <>
                {/* Company Name */}
                <FormGroup
                  name="name"
                  value={values.name}
                  displayName="Entity Name"
                  error={errors.name}
                  touched={touched.name}
                  handleChange={handleChange}
                  inputWidth={s.widthFull}
                  tooltipInfo="Use the entity name registered with the IRS."
                />
                <div css={s.formTitle}>Billing Contact</div>
                {/* Contact Name */}
                <FormGroup
                  className="pii-encrypt"
                  name="contact.name"
                  value={values.contact.name}
                  displayName="Name"
                  error={errors.contact?.name}
                  touched={touched.contact?.name}
                  handleChange={handleChange}
                  inputWidth={s.widthFull}
                />
                {/* Contact Email */}
                <FormGroup
                  className="pii-encrypt"
                  name="contact.emailAddress"
                  value={values.contact.emailAddress}
                  displayName="Accounts Payable Email"
                  error={errors.contact?.emailAddress}
                  touched={touched.contact?.emailAddress}
                  handleChange={handleChange}
                  inputWidth={s.widthFull}
                />
                {/* Contact Phone */}
                <FormGroup
                  className="pii-encrypt"
                  name="contact.phoneNumber"
                  value={values.contact.phoneNumber}
                  displayName="Accounts Payable Phone Number"
                  error={errors.contact?.phoneNumber}
                  touched={touched.contact?.phoneNumber}
                  handleChange={handleChange}
                  inputWidth={s.widthFull}
                />
              </>
            )}
            <div css={s.formTitle}>Billing Address</div>
            {/* Street 1 */}
            <FormGroup
              className="pii-encrypt"
              name="contact.address.street1"
              value={values.contact.address.street1}
              displayName="Street Address"
              error={errors.contact?.address?.street1}
              touched={touched.contact?.address?.street1}
              handleChange={handleChange}
              inputWidth={[s.widthFull]}
            />
            {/* Street 2 */}
            <Link
              onClick={() => setShowStreet2(!showStreet2)}
              css={css`
                &:hover {
                  text-decoration: none;
                }
              `}
            >
              {showStreet2 ? (
                <i className="fa fa-minus" />
              ) : (
                <i className="fa fa-plus" />
              )}{' '}
              Add Apt, Suite, or Other
            </Link>
            {showStreet2 && (
              <FormGroup
                className="pii-encrypt"
                name="contact.address.street2"
                value={values.contact.address.street2}
                error={errors.contact?.address?.street2}
                touched={touched.contact?.address?.street2}
                handleChange={handleChange}
                inputWidth={[s.widthFull]}
              />
            )}
            <Box mt={theme.space[2]}>
              <Label>Country</Label>
              <ReactSelect
                className="pii-encrypt"
                id="country"
                value={{
                  value: values.contact.address.country,
                  label: values.contact.address.country,
                }}
                options={countryListUtil.countryList}
                styles={reactSelectStyle}
                theme={selectTheme}
                onChange={(selectedOption) => {
                  setFieldValue(
                    'contact.address.country',
                    selectedOption?.label
                  );
                }}
              />
              <Flex justifyContent="space-between" mt={theme.space[2]}>
                {/* City */}
                <FormGroup
                  className="pii-encrypt"
                  name="contact.address.city"
                  value={values.contact.address.city}
                  displayName="City"
                  required
                  error={errors.contact?.address?.city}
                  touched={touched.contact?.address?.city}
                  handleChange={handleChange}
                  inputWidth={[s.widthFull]}
                  boxStyle={[s.inline, s.widthThird]}
                />
                {/* State */}
                {values.contact.address.country === 'United States' ? (
                  <Box
                    css={[s.inline, s.widthQuarter, s.paddingLeftRight]}
                    marginBottom={theme.space[2]}
                  >
                    <Label>State</Label>
                    <Select
                      className="pii-encrypt"
                      id="state"
                      value={values.contact.address.state}
                      backgroundColor={theme.colors.white}
                      onChange={(selectedOption) => {
                        setFieldValue(
                          'contact.address.state',
                          selectedOption.target.value
                        );
                      }}
                    >
                      <option disabled hidden value="" label="Select" />
                      {stateOptions}
                    </Select>
                    {errors.contact?.address?.state &&
                      touched.contact?.address?.state && (
                        <FormMessage invalid mt={1}>
                          {errors.contact?.address?.state}
                        </FormMessage>
                      )}
                  </Box>
                ) : (
                  <FormGroup
                    className="pii-encrypt"
                    name="contact.address.state"
                    value={values.contact.address.state}
                    displayName="State"
                    error={errors.contact?.address?.state}
                    touched={touched.contact?.address?.state}
                    handleChange={handleChange}
                    inputWidth={[s.widthFull]}
                    boxStyle={[s.inline, s.widthQuarter, s.paddingLeftRight]}
                  />
                )}
                {/* Zipcode */}
                <FormGroup
                  className="pii-encrypt"
                  name="contact.address.zipcode"
                  value={values.contact.address.zipcode}
                  displayName="Zipcode"
                  error={errors.contact?.address?.zipcode}
                  touched={touched.contact?.address?.zipcode}
                  handleChange={handleChange}
                  inputWidth={[s.widthFull]}
                  boxStyle={[s.inline, s.widthThird]}
                />
              </Flex>
            </Box>
            <Flex mt={3} justifyContent="flex-end">
              <Button
                onClick={onCancelClick}
                kind={theme.kind.primaryOutline}
                mr={theme.space[2]}
                type="reset"
              >
                Cancel
              </Button>
              <Button kind="primary" type="submit" disabled={loading}>
                {isEdit ? 'Submit' : 'Create Billing Profile'}
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
      <Alert
        mt={theme.space[4]}
        isOpen={submitErrors}
        width="300px"
        kind={theme.kind.danger}
      >
        {submitErrors}
      </Alert>
    </Panel>
  );
};

export default BillingProfileForm;
